import type { CheckoutError } from '@/lib/types';

interface Props {
  error: CheckoutError;
  canRetry: boolean;
  onRetry: () => void;
  cancelUrl: string;
}

const ERROR_MESSAGES: Record<string, string> = {
  INSUFFICIENT_FUNDS: 'Fondos insuficientes. Intenta con otro método de pago.',
  WALLET_INSUFFICIENT_FUNDS: 'Saldo insuficiente en tu billetera. Elige otro método de pago.',
  CARD_DECLINED: 'Tarjeta rechazada. Verifica los datos o usa otra tarjeta.',
  CARD_EXPIRED: 'La tarjeta ha expirado. Usa otra tarjeta.',
  INVALID_CARD: 'Los datos de la tarjeta no son válidos. Revísalos e intenta de nuevo.',
  POLL_TIMEOUT: 'El pago está tardando más de lo esperado. Puedes intentar de nuevo o verificar tu email.',
  SERVER_ERROR: 'Ocurrió un error inesperado. Por favor intenta de nuevo.',
  PROVIDER_ERROR: 'El proveedor de pago rechazó la operación. Intenta con otro método.',
  NETWORK_ERROR: 'Problema de conexión. Verifica tu internet e intenta de nuevo.',
  TIMEOUT: 'La operación tardó demasiado. Por favor intenta de nuevo.',
  UNSUPPORTED_PAYMENT_METHOD: 'Este método de pago no está disponible para este pedido.',
  SESSION_EXPIRED_DURING_PAYMENT: 'La sesión expiró durante el pago. Regresa a la tienda para intentar de nuevo.',
  CANCELLED_BY_PROVIDER: 'El pago fue cancelado por el proveedor. Elige otro método de pago.',
};

function friendlyMessage(error: CheckoutError): string {
  return (
    ERROR_MESSAGES[error.code] ??
    (error.message && !error.message.startsWith('HTTP') && error.message.length < 200
      ? error.message
      : 'No pudimos procesar tu pago. Intenta nuevamente o elige otro método.')
  );
}

export function Failure({ error, canRetry, onRetry, cancelUrl }: Props) {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mx-auto mb-6">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-2">Pago fallido</h2>
        <p className="text-gray-500 mb-6">{friendlyMessage(error)}</p>

        <div className="space-y-3">
          {canRetry && (
            <button
              onClick={onRetry}
              className="w-full bg-brand-600 text-white font-semibold py-3.5 px-6 rounded-xl hover:bg-brand-700 active:scale-95 transition-all min-h-[52px]"
            >
              Intentar de nuevo
            </button>
          )}
          <a
            href={cancelUrl}
            className="block w-full text-gray-600 font-medium py-3.5 px-6 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-center min-h-[52px] flex items-center justify-center"
          >
            Volver a la tienda
          </a>
        </div>
      </div>
    </div>
  );
}
